// src/components/CalculateMoney/OverviewPanel.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, MenuItem, Select, FormControl, InputLabel,
  CircularProgress, TextField, Stack, Typography, Button,
  List, Collapse, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useCalculateMoneyContext } from '../../context/CalculateMoneyContext';
import { useWorkSessionsContext } from '../../context/WorkSessionsContext';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import WorkSessionListForDay from './views/WorkSessionListForDay';
import OverviewTodayView from './views/OverviewTodayView';
import OverviewDayView from './views/OverviewDayView';
import OverviewWeekView from './views/OverviewWeekView';
import OverviewMonthView from './views/OverviewMonthView';
import OverviewYearView from './views/OverviewYearView';
import WeekPicker from './Pickers/WeekPicker';

import {
  boxStyle,
  stackStyle,
  formControlPeriodStyle,
  formControlMonthStyle,
  formControlYearStyle,
  yearSelectSx,
  loadingBoxStyle,
  weekPickerBoxStyle,
  textFieldDayStyle,
  transitionProps,
  staffListScrollable,
  noStaffFoundText,
  staffHistoryDetailBox,
  openStaffCardSx,
  closedStaffCardSx,
} from '../../styles/CalculateMoney/OverviewPanelStyles';

import {
  CategoryCardStyled as StaffCardStyled,
  ListItemTextStyled,
} from '../../styles/OwnerStyle/ServiceComponent/ServiceListStyles';

// we just keep the values here; labels come from i18n
const periodValues = ['year', 'month', 'week', 'day', 'today'];

const yearList = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 9 + i);

function toSessionArray(sessions) {
  if (Array.isArray(sessions)) return sessions;
  if (sessions && Array.isArray(sessions.$values)) return sessions.$values;
  return [];
}

function getFinishedSessionsFor(workSessions, staffId, dateString) {
  const sessions = toSessionArray(workSessions);
  return sessions.filter(ws =>
    Number(ws.staffId) === Number(staffId) &&
    ws.status?.toLowerCase() === 'finished' &&
    ws.sessionStart &&
    ws.sessionStart.slice(0, 10) === dateString
  );
}

const OverviewPanel = ({
  businessId,
  staff = [],
  currentWeek,
  workSessions = [],
  services,
  categories,
  onShowSuccess
}) => {
  const { t } = useTranslation('overviewPanel');

  const {
    businessEarnings,
    businessTodaySummary,
    businessDailyEarnings,
    getBusinessYearlyEarnings,
    getBusinessMonthlyEarnings,
    getBusinessWeeklyEarnings,
    getBusinessDailyEarnings,
    getBusinessTodaySummary
  } = useCalculateMoneyContext();

  const now = useMemo(() => new Date(), []);
  const [period, setPeriod] = useState('today');
  const [selectedDate, setSelectedDate] = useState(format(now, 'yyyy-MM-dd'));
  const [selectedWeek, setSelectedWeek] = useState(currentWeek);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedYearsBack, setSelectedYearsBack] = useState(5);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const workSessionArr = useMemo(() => toSessionArray(workSessions), [workSessions]);
  const { updateWorkSessionAndRefresh } = useWorkSessionsContext();

  const businessServices = useMemo(() => {
    if (Array.isArray(workSessionArr) && workSessionArr[0]?.business?.services?.$values) {
      return workSessionArr[0].business.services.$values;
    }
    return [];
  }, [workSessionArr]);

  useEffect(() => {
    if (!businessId) return;
    setLoading(true);
    let promise;
    if (period === 'year') {
      promise = getBusinessYearlyEarnings(businessId, selectedYearsBack);
    } else if (period === 'month') {
      promise = getBusinessMonthlyEarnings(businessId, selectedYear);
    } else if (period === 'week') {
      promise = getBusinessWeeklyEarnings(businessId, selectedYear, selectedWeek);
    } else if (period === 'day') {
      promise = getBusinessDailyEarnings(businessId, selectedDate);
    } else if (period === 'today') {
      promise = getBusinessTodaySummary(businessId);
    }
    Promise.resolve(promise).finally(() => setLoading(false));
  }, [
    businessId, period, selectedDate, selectedWeek, selectedYear, selectedYearsBack,
    getBusinessYearlyEarnings, getBusinessMonthlyEarnings,
    getBusinessWeeklyEarnings, getBusinessDailyEarnings, getBusinessTodaySummary
  ]);

  useEffect(() => {
    setShowHistory(false);
    setSelectedStaffId(null);
  }, [period, selectedDate]);

  function renderStaffHistory(dateString) {
    if (!Array.isArray(staff) || staff.length === 0) {
      return (
        <div style={noStaffFoundText}>
          {t('noStaffFound')}
        </div>
      );
    }
    return staff.map(staffMember => {
      const isOpen = selectedStaffId === staffMember.staffId;
      return (
        <React.Fragment key={staffMember.staffId}>
          <StaffCardStyled
            button
            onClick={() => setSelectedStaffId(isOpen ? null : staffMember.staffId)}
            sx={isOpen ? openStaffCardSx : closedStaffCardSx}
          >
            <ListItemTextStyled
              primary={
                <Typography variant="h6">
                  {(staffMember.firstName || staffMember.name || '') + ' ' + (staffMember.lastName || '')}
                </Typography>
              }
              secondary={
                <Typography variant="body2" color="textSecondary">
                  {staffMember.email}
                </Typography>
              }
            />
            <ChevronRightIcon
              style={{
                marginLeft: 'auto',
                transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
              }}
            />
          </StaffCardStyled>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={staffHistoryDetailBox}>
              <WorkSessionListForDay
                workSessions={getFinishedSessionsFor(workSessionArr, staffMember.staffId, dateString)}
                businessServices={businessServices}
                businessId={businessId}
                onUpdateWorkSession={updateWorkSessionAndRefresh}
                services={services}
                categories={categories}
                onShowSuccess={onShowSuccess}
                editable={true}
              />
            </Box>
          </Collapse>
        </React.Fragment>
      );
    });
  }

  return (
    <Box sx={boxStyle}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={stackStyle}>
        <FormControl variant="outlined" size="small" sx={formControlPeriodStyle}>
          <InputLabel>{t('period')}</InputLabel>
          <Select
            label={t('period')}
            value={period}
            onChange={e => setPeriod(e.target.value)}
          >
            {periodValues.map(val => (
              <MenuItem key={val} value={val}>
                {t(`periodOptions.${val}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {period === 'month' && (
          <FormControl size="small" sx={formControlMonthStyle}>
            <Select
              value={selectedYear}
              onChange={e => setSelectedYear(Number(e.target.value))}
              displayEmpty
              sx={yearSelectSx}
            >
              {yearList.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {period === 'year' && (
          <FormControl size="small" sx={formControlYearStyle}>
            <Select
              value={selectedYearsBack}
              onChange={e => setSelectedYearsBack(Number(e.target.value))}
              displayEmpty
              sx={yearSelectSx}
            >
              {[...Array(10)].map((_, n) => (
                <MenuItem key={n + 1} value={n + 1}>
                  {t('yearsBack.xYears', { count: n + 1 })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {period === 'day' && (
          <TextField
            type="date"
            size="small"
            label={t('pickDateLabel')}
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={textFieldDayStyle}
          />
        )}

        {period === 'week' && (
          <Box sx={weekPickerBoxStyle}>
            <WeekPicker
              year={selectedYear}
              week={selectedWeek}
              onChange={(year, week) => {
                setSelectedYear(year);
                setSelectedWeek(week);
              }}
            />
          </Box>
        )}
      </Stack>

      {loading ? (
        <Box sx={loadingBoxStyle}>
          <CircularProgress />
        </Box>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={`${period}-${period === 'day'
              ? selectedDate
              : period === 'week'
                ? `${selectedYear}-${selectedWeek}`
                : period === 'month'
                  ? selectedYear
                  : period === 'year'
                    ? selectedYearsBack
                    : ''}`}
            {...transitionProps}
          >
            {period === 'today' && (
              <>
                <OverviewTodayView data={businessTodaySummary} />
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('today.finishedAllTitle')}
                  </Typography>
                  <Tooltip title={showHistory ? t('buttons.hide') : t('buttons.show')}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setShowHistory(x => !x)}
                        startIcon={showHistory ? <RemoveIcon /> : <AddIcon />}
                        sx={{
                          minWidth: 0,
                          maxWidth: { xs: 110, sm: 150 },
                          fontSize: { xs: 11, sm: 13 },
                          px: 1.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {showHistory ? t('buttons.hide') : t('buttons.show')}
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
                <Collapse in={showHistory}>
                  <List dense sx={staffListScrollable}>
                    {renderStaffHistory(format(now, 'yyyy-MM-dd'))}
                  </List>
                </Collapse>
              </>
            )}

            {period === 'day' && (
              <>
                <OverviewDayView data={businessDailyEarnings} date={selectedDate} />
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('day.finishedOnDateTitle', { date: selectedDate })}
                  </Typography>
                  <Tooltip title={showHistory ? t('buttons.hide') : t('buttons.show')}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setShowHistory(x => !x)}
                        startIcon={showHistory ? <RemoveIcon /> : <AddIcon />}
                        sx={{
                          minWidth: 0,
                          maxWidth: { xs: 110, sm: 150 },
                          fontSize: { xs: 11, sm: 13 },
                          px: 1.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {showHistory ? t('buttons.hide') : t('buttons.show')}
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
                <Collapse in={showHistory}>
                  <List dense sx={staffListScrollable}>
                    {renderStaffHistory(selectedDate)}
                  </List>
                </Collapse>
              </>
            )}

            {period === 'week' && (
              <OverviewWeekView data={businessEarnings} week={selectedWeek} year={selectedYear} />
            )}

            {period === 'month' && (
              <OverviewMonthView data={businessEarnings || []} year={selectedYear} onYearChange={setSelectedYear} />
            )}

            {period === 'year' && (
              <OverviewYearView
                data={businessEarnings}
                yearsBack={selectedYearsBack}
                currentYear={new Date().getFullYear()}
              />
            )}
          </motion.div>
        </AnimatePresence>
      )}
    </Box>
  );
};

export default OverviewPanel;
